// One table row: a left gutter of row-level controls, then the data cells.
// Everything in the gutter acts on the row without opening the config panel.

import { memo } from 'react';
import {
  Badge,
  Box,
  HStack,
  Icon,
  IconButton,
  Td,
  Text,
  Tooltip,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  MdApproval,
  MdContentCopy,
  MdDragIndicator,
  MdEdit,
  MdQrCode2,
  MdSettings,
} from 'react-icons/md';
import { GoChecklist } from 'react-icons/go';
import { SiReacthookform } from 'react-icons/si';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useNavigate } from 'react-router-dom';
import { ThemedCheckbox } from '@/design-system/components/ThemedCheckbox';
import { Chatbot } from '@/lib/sdk/chatbot/types';
import { getFlowState, startCommand } from './flowState';
import { TableChip } from './TableChip';

interface WorkflowTableRowProps {
  chatbot: Chatbot;
  /** 1-based position in the table as displayed. */
  position: number;
  isChecked: boolean;
  isOpen: boolean;
  isDraggable: boolean;
  reminderSummary: string;
  onCheck: (id: string) => void;
  onConfigure: (chatbot: Chatbot) => void;
  onCopyCommand: (chatbot: Chatbot) => void;
  onShowQr: (chatbot: Chatbot) => void;
}

export const WorkflowTableRow = memo(function WorkflowTableRow({
  chatbot,
  position,
  isChecked,
  isOpen,
  isDraggable,
  reminderSummary,
  onCheck,
  onConfigure,
  onCopyCommand,
  onShowQr,
}: WorkflowTableRowProps) {
  const navigate = useNavigate();
  // Same row gradient design-system/components/DataTable uses, so this table reads as
  // part of the app rather than a one-off.
  const rowGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: chatbot.id,
    disabled: !isDraggable,
  });

  const flowState = getFlowState(chatbot);
  const isChecklist = chatbot.bot_type === 'static';

  return (
    <Tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`wfl-row-${chatbot.id}`}
      role="group"
      bgGradient={isOpen ? undefined : rowGradient}
      bg={isOpen ? 'bg-surface-hover' : undefined}
      opacity={isDragging ? 0.5 : 1}
      _hover={{ bg: 'bg-surface-hover' }}
      // The open row is marked with a left accent rather than a brand tint: the tint
      // used the same token as the chips, which made them vanish into the row.
      sx={{
        td: { borderColor: 'border-default' },
        'td:first-of-type': {
          borderLeftWidth: isOpen ? '3px' : '0',
          borderLeftColor: 'brand.500',
          borderLeftStyle: 'solid',
        },
      }}
    >
      {/* Left gutter — select, reorder, configure */}
      <Td>
        <HStack spacing={1}>
          <ThemedCheckbox
            data-testid={`wfl-row-checkbox-${chatbot.id}`}
            isChecked={isChecked}
            onChange={() => onCheck(chatbot.id)}
            aria-label={`Select ${chatbot.title}`}
          />
          <Box
            {...(isDraggable ? attributes : {})}
            {...(isDraggable ? listeners : {})}
            cursor={isDraggable ? 'grab' : 'default'}
            _active={{ cursor: isDraggable ? 'grabbing' : 'default' }}
            display="flex"
            alignItems="center"
            data-testid={`wfl-drag-${chatbot.id}`}
            aria-label={isDraggable ? `Reorder ${chatbot.title}` : undefined}
          >
            <Icon
              as={MdDragIndicator}
              boxSize={4}
              color="brand.500"
              opacity={0}
              _groupHover={{ opacity: isDraggable ? 0.5 : 0 }}
              transition="opacity 0.15s"
            />
          </Box>
          <Text fontSize="xs" fontWeight="medium" color="text-secondary" minW="18px">
            {position}
          </Text>
          <Tooltip label="Configure" placement="top" openDelay={300}>
            <IconButton
              size="xs"
              variant="ghost"
              data-testid={`wfl-configure-${chatbot.id}`}
              aria-label={`Configure ${chatbot.title}`}
              icon={<Icon as={MdSettings} boxSize={4} />}
              color="brand.500"
              onClick={() => onConfigure(chatbot)}
            />
          </Tooltip>
        </HStack>
      </Td>

      <Td>
        <HStack spacing={2} minW={0}>
          <Text
            as="button"
            type="button"
            fontWeight="semibold"
            color="text-primary"
            textAlign="left"
            onClick={() => onConfigure(chatbot)}
            _hover={{ color: 'brand.500' }}
          >
            {chatbot.title}
          </Text>
          {chatbot.task_approvals && (
            <Tooltip label="Task approvals enabled" placement="top">
              <span><Icon as={MdApproval} boxSize={3.5} color="purple.400" /></span>
            </Tooltip>
          )}
        </HStack>
      </Td>

      <Td>
        {chatbot.department_label ? (
          <Badge data-testid={`wfl-department-${chatbot.id}`} variant="outline" colorScheme="gray" fontWeight="normal" textTransform="none">
            {chatbot.department_label}
          </Badge>
        ) : (
          <Text fontSize="sm" color="text-secondary">—</Text>
        )}
      </Td>

      {/* The command users actually send. Its own column so it can be read down the
          list and copied without opening anything. */}
      <Td>
        <TableChip
          label={startCommand(chatbot.short_code)}
          icon={MdContentCopy}
          tooltip="Copy command"
          ariaLabel={`Copy command for ${chatbot.title}`}
          testId={`wfl-command-${chatbot.id}`}
          onClick={() => onCopyCommand(chatbot)}
          isMono
        />
      </Td>

      <Td>
        <HStack spacing={1.5}>
          <Icon
            as={isChecklist ? GoChecklist : SiReacthookform}
            boxSize={3.5}
            color="brand.500"
          />
          <Text fontSize="sm">{isChecklist ? 'Checklist' : 'Form'}</Text>
          {flowState && (
            <Badge colorScheme={flowState.colorScheme} fontSize="2xs" whiteSpace="nowrap">
              {flowState.label}
            </Badge>
          )}
        </HStack>
      </Td>

      {/* Straight to the task editor. Going via the panel and the edit-method dialog
          cost three clicks to reach the same screen. Styled as a chip like Command so
          the two actionable cells read the same way. */}
      <Td>
        <TableChip
          label={String(chatbot.task_count)}
          icon={MdEdit}
          tooltip="Update tasks"
          ariaLabel={`Update tasks for ${chatbot.title}`}
          testId={`wfl-tasks-${chatbot.id}`}
          onClick={() =>
            navigate(`/dashboard/${chatbot.org_id}/workflows/${chatbot.id}/edit-v4`)
          }
        />
      </Td>

      <Td>
        <Text
          fontSize="sm"
          whiteSpace="nowrap"
          color={chatbot.reminders?.length ? 'text-primary' : 'text-secondary'}
        >
          {reminderSummary}
        </Text>
      </Td>

      {/* Spacer — pairs with the empty header cell so slack collects here. */}
      <Td />

      {/* QR is a printing/sharing action, so it is deliberately sized larger than the
          gutter icons: it is the one control people hunt for down the column. */}
      <Td>
        <Tooltip label="QR code & link" placement="top" openDelay={300}>
          <IconButton
            size="md"
            variant="ghost"
            data-testid={`wfl-qr-${chatbot.id}`}
            aria-label={`QR code for ${chatbot.title}`}
            icon={<Icon as={MdQrCode2} boxSize={7} />}
            color="brand.500"
            onClick={() => onShowQr(chatbot)}
          />
        </Tooltip>
      </Td>
    </Tr>
  );
});
